import { siteFeatureFlagKeys, readBootstrappedFeatureFlagsPayload } from "@shared/hooks/use-site-feature-flags";
import { resolveEntitledLandingPath } from "@shared/hooks/use-site-feature-flags/useLandingPath";
import {
  getConfiguredFeatureRoutes,
  type SiteFeatureFlags,
  type SiteFeatureFlagsPayload,
} from "@shared/lib/site-features";

import { consumeReturnUrl } from "./lib/guestMode/returnUrl";
import { getQueryClient } from "./providers/QueryProvider";
import { readPaymentConfigSnapshot } from "./routeAvailability";

function knownFlags(): SiteFeatureFlags | null {
  const cached = getQueryClient().getQueryData<SiteFeatureFlagsPayload>(siteFeatureFlagKeys.all);
  return cached?.featureFlags ?? readBootstrappedFeatureFlagsPayload()?.featureFlags ?? null;
}

/**
 * Flags not known yet (no SSR bootstrap, empty cache): aim for the top-ranked
 * landing route; the router's feature guard fetches the real flags and, if
 * that feature is off, redirects on to the first enabled one.
 */
function optimisticFlags(): SiteFeatureFlags {
  const all: Record<string, boolean> = {};
  for (const route of getConfiguredFeatureRoutes()) {
    if (route.feature) all[route.feature] = true;
  }
  return all;
}

/**
 * Resolve the normal authenticated landing page, honoring a safe `next` path.
 *
 * The landing page is the first *enabled* feature by `landingPriority`
 * (Health Assistant → Diet → Dashboard → the rest in sidebar order) that the
 * paywall/BYOK layer also allows — never a disabled page.
 */
export function getPostLoginDestination(): string {
  const carriedNext = consumeReturnUrl();
  if (carriedNext) return carriedNext;
  return resolveEntitledLandingPath(
    knownFlags() ?? optimisticFlags(),
    readPaymentConfigSnapshot(getQueryClient()),
  );
}
